import type { JSX } from 'react';

/**
 * N5.8 / R8 (round-6) — Activity indicators.
 *
 * Compact row of live metrics for an agent:
 *   - tokens/sec throughput
 *   - active tool call (or `—`)
 *   - Ralph iteration counter (current / max)
 *   - context-window bar (% filled)
 *   - provider latency sparkline (last 20 samples)
 *
 * The data shape is fed by `/ws/agent-activity` payloads. This
 * component is presentational only; callers own the subscription.
 */
export interface ActivityIndicatorsProps {
  tokensPerSec: number;
  activeTool: string | null;
  ralphIteration: number | null;
  ralphMax: number | null;
  contextWindowUsed: number; // 0..1
  latencyMsSeries: number[];
}

export function ActivityIndicators({
  tokensPerSec,
  activeTool,
  ralphIteration,
  ralphMax,
  contextWindowUsed,
  latencyMsSeries,
}: ActivityIndicatorsProps): JSX.Element {
  const pct = Math.max(0, Math.min(1, contextWindowUsed));
  const pctLabel = `${Math.round(pct * 100)}%`;

  return (
    <div
      className="grid grid-cols-5 gap-3 text-2xs"
      aria-label="Agent activity indicators"
    >
      <Cell label="tok/s" value={tokensPerSec.toFixed(1)} />
      <Cell label="tool" value={activeTool ?? '—'} mono />
      <Cell
        label="ralph"
        value={
          ralphIteration !== null && ralphMax !== null
            ? `${ralphIteration}/${ralphMax}`
            : '—'
        }
        mono
      />
      <div className="flex flex-col gap-1">
        <span className="uppercase tracking-wider text-fg-muted">ctx</span>
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pct * 100)}
          className="h-1.5 w-full rounded bg-bg-raised"
        >
          <div
            className="h-full rounded bg-accent"
            style={{ width: pctLabel }}
          />
        </div>
        <span className="shakti-mono text-fg-muted">{pctLabel}</span>
      </div>
      <div className="flex flex-col gap-1">
        <span className="uppercase tracking-wider text-fg-muted">latency</span>
        <Sparkline values={latencyMsSeries} />
      </div>
    </div>
  );
}

function Cell({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <span className="uppercase tracking-wider text-fg-muted">{label}</span>
      <span className={mono ? 'shakti-mono text-fg-primary' : 'text-fg-primary'}>
        {value}
      </span>
    </div>
  );
}

function Sparkline({ values }: { values: number[] }): JSX.Element {
  if (values.length === 0) {
    return <span className="text-fg-muted">—</span>;
  }
  const max = Math.max(...values, 1);
  const min = Math.min(...values);
  const range = Math.max(1, max - min);
  const width = 60;
  const height = 18;
  const step = width / Math.max(1, values.length - 1);
  const points = values
    .map((v, i) => {
      const x = i * step;
      const y = height - ((v - min) / range) * height;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg
      role="img"
      aria-label={`latency sparkline, last ${values.length} samples`}
      width={width}
      height={height}
      className="text-accent"
    >
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        points={points}
      />
    </svg>
  );
}
